import { EventEmitter, Type } from "@angular/core";

export interface WidgetConfig {
    widgetMode: boolean;
    widgetId?: string;
    gridViewId?: string;
}

export interface GridWidgetConfig {
    objectId: string;
    gridViewId?: string;
    viewName?: string;
}

export interface IWidgetConfigComponent {
    config: Record<string, unknown>;
    configChange: EventEmitter<Record<string, unknown>>;
}

export interface DashboardWidgetDefinition {
    type: string;
    name: string;
    description: string;
    icon: string;
    category: WidgetCategory;
    component: Type<IDashboardWidget>;
    configComponent?: Type<IWidgetConfigComponent>;
    minWidth: number;
    minHeight: number;
    defaultWidth: number;
    defaultHeight: number;
    maxWidth?: number;
    maxHeight?: number;
    configSchema: WidgetConfigField[];
    previewImage?: string;
    requiredPermission?: string | string[];
}

export type WidgetCategory =
    | "statistics"
    | "charts"
    | "lists"
    | "maps"
    | "actions"
    | "other";

export interface WidgetCategoryInfo {
    key: WidgetCategory;
    name: string;
    icon: string;
    order: number;
}

export const WIDGET_CATEGORIES: WidgetCategoryInfo[] = [
    {
        key: "statistics",
        name: "Statistics",
        icon: "analytics",
        order: 1,
    },
    {
        key: "charts",
        name: "Charts",
        icon: "bar_chart",
        order: 2,
    },
    {
        key: "lists",
        name: "Lists",
        icon: "list",
        order: 3,
    },
    { key: "maps", name: "Maps", icon: "map", order: 4 },
    {
        key: "actions",
        name: "Quick Actions",
        icon: "touch_app",
        order: 5,
    },
    {
        key: "other",
        name: "Other",
        icon: "widgets",
        order: 6,
    },
];

export interface WidgetConfigField {
    key: string;
    label: string;
    type: WidgetConfigFieldType;
    default: unknown;
    options?: WidgetConfigOption[];
    entityType?: string;
    required?: boolean;
    helpText?: string;
    validation?: WidgetConfigValidation;
    showWhen?: { field: string; value: unknown };
}

export type WidgetConfigFieldType =
    | "text"
    | "number"
    | "select"
    | "multi-select"
    | "boolean"
    | "color"
    | "date-range"
    | "entity-picker";

export interface WidgetConfigOption {
    value: unknown;
    label: string;
    icon?: string;
}

export interface WidgetConfigValidation {
    min?: number;
    max?: number;
    pattern?: string;
    message?: string;
}

export interface IDashboardWidget {
    widgetConfig: Record<string, unknown>;
    widgetTitle?: string;
    isLoading: boolean;
    error?: string;
    refresh(): void;
    onConfigChange?(config: Record<string, unknown>): void;
}

export interface WidgetDataContext {
    organizationLevelId?: string;
    dateRange?: { start: Date; end: Date };
    filters?: Record<string, unknown>;
}

export const WIDGET_SIZE_PRESETS = {
    small: { width: 1, height: 1 },
    medium: { width: 2, height: 1 },
    large: { width: 2, height: 2 },
    wide: { width: 3, height: 1 },
    tall: { width: 1, height: 2 },
    full: { width: 3, height: 2 },
    xl: { width: 4, height: 4 },
    banner: { width: 8, height: 2 },
} as const;

export type WidgetSizePreset = keyof typeof WIDGET_SIZE_PRESETS;

export const WIDGET_SIZE_PRESET_LABELS: Record<WidgetSizePreset, string> = {
    small: "Small",
    medium: "Medium",
    large: "Large",
    wide: "Wide",
    tall: "Tall",
    full: "Full",
    xl: "Extra Large",
    banner: "Banner",
};
